import {
  CONFIGS,
  getCustomFieldsDefaultValues,
  getCustomFieldsValidationSchema,
  updateConfig,
  useConfigQuery,
} from '@buildwise/libs/configurations';
import UserAPI from 'api/UserAPI';
import { ConfigEntryType, ConfigFieldEntryType } from 'config/config';
import useUserData, { UserType } from 'hooks/useUserData';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { catchErrorMessage } from 'utility/helpers';
import { updateUserDetailsCache } from '../queries';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { CustomFieldValueType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { getPredefinedFieldValidation } from '../helpers/user-form.helpers';
import useMultiConfigEntryOptionsQuery from '@buildwise/libs/configurations/queries/useMultiConfigEntryOptionsQuery';

export type EditProfileFormSchema = {
  firstname: string;
  lastname: string;
  email: string;
};

export const editProfileSchema = (customFields: ConfigEntryType[]) =>
  Yup.object().shape({
    ...getCustomFieldsValidationSchema({
      fields: customFields,
      getPredefinedFieldValidation: ({ field }) => getPredefinedFieldValidation({ field }),
    }),
    firstname: Yup.string()
      .min(2, 'The first name must contain at least 2 characters')
      .required('Required field')
      .nullable(),
    lastname: Yup.string()
      .min(1, 'The last name must be at least one character')
      .required('Required field')
      .nullable(),
    email: Yup.string().email('Enter a valid email').required('Required field'),
  });

function useProfileForm({
  user,
  setOpen,
}: {
  user: UserType;
  setOpen: Dispatch<SetStateAction<boolean>>;
}) {
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const { companyId } = useUserData();
  const { activeUniqueEntries: customInputFields, isFetched: isCustomFieldsFetched } =
    useConfigQuery({
      companyId,
      configName: CONFIGS.USER_INPUT_FIELDS.configName,
    });
  const getDefaultValues = () => {
    return {
      ...getCustomFieldsDefaultValues({ data: user, fields: customInputFields }),
      firstname: user.firstname,
      lastname: user.lastname,
      email: user.email,
    };
  };
  const profileForm = useForm<EditProfileFormSchema>({
    resolver: yupResolver(editProfileSchema(customInputFields)),
    defaultValues: getDefaultValues(),
  });
  const {
    formState: { isSubmitting, isDirty, dirtyFields },
    handleSubmit,
    reset,
  } = profileForm;

  const freeSoloFields = useMemo(() => {
    return customInputFields?.filter(
      (field) => field?.referenceObject?.configRefName && field?.referenceObject?.freeText,
    );
  }, [customInputFields]);

  const { configsValues } = useMultiConfigEntryOptionsQuery({
    companyId,
    configEntries: freeSoloFields?.map((configEntry) => ({
      configEntry,
    })),
    shouldFetch: freeSoloFields.length > 0,
  });

  useEffect(() => {
    if (customInputFields.length > 0) {
      reset(getDefaultValues(), {
        keepDirtyValues: true,
        keepErrors: true,
        keepTouched: true,
      });
    }
  }, [customInputFields]);

  const closeModal = useCallback(() => {
    reset();
    setOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    if (isDirty && Object.keys(dirtyFields).length) {
      setOpenConfirmation(true);
    } else {
      closeModal();
    }
  }, [isDirty, dirtyFields, closeModal]);

  const onSubmit = useCallback(
    handleSubmit(
      async (values: EditProfileFormSchema) => {
        try {
          const payload = {
            ...user,
            firstname: values.firstname,
            lastname: values.lastname,
            email: values.email,
          };
          const saveAsLists = ['department', 'region'];

          // Custom field values
          const customFieldsValues: Record<string, CustomFieldValueType> = {};
          const userKeys = Object.keys(user);
          customInputFields.forEach((field) => {
            if (
              Array.isArray(values[field.entryCode])
                ? values[field.entryCode].length > 0
                : values[field.entryCode]
            ) {
              if (
                userKeys.includes(field.entryCode) ||
                field.entryType?.toUpperCase() !== ConfigFieldEntryType.CUSTOM
              ) {
                payload[field.entryCode] =
                  saveAsLists.includes(field.entryCode) && field.referenceObject?.type === 'SELECT'
                    ? [values[field.entryCode]]
                    : values[field.entryCode];
              } else {
                customFieldsValues[field.entryCode] = {
                  fieldName: field.entryCode,
                  fieldValue:
                    saveAsLists.includes(field.entryCode) &&
                    field.referenceObject?.type === 'SELECT'
                      ? [values[field.entryCode]]
                      : values[field.entryCode],
                  type: field.referenceObject?.type || '',
                  configRefName: field.referenceObject?.configRefName || '',
                  label: field.referenceObject?.label || field.entryDescription,
                };
              }
            }
          });
          payload['customFields'] = customFieldsValues;

          // Adding new values to the config
          freeSoloFields?.forEach((field) => {
            const config = configsValues[field.entryCode]?.queryResponse?.data?.data?.config;
            if (dirtyFields[field.entryCode] && !!config) {
              updateConfig({ newValue: payload[field.entryCode], config, companyId });
            }
          });
          const { data } = await UserAPI.editUser(payload);
          updateUserDetailsCache({
            ...user,
            ...data,
            userNameToDisplay: `${data.firstname} ${data.lastname}`,
          });
          toast.success('Profile is updated');
          closeModal();
        } catch (e) {
          toast.error(catchErrorMessage(e));
        }
      },
      () => {
        // Close action modal
        setOpenConfirmation(false);
      },
    ),
    [
      user,
      customInputFields,
      closeModal,
      dirtyFields,
      freeSoloFields,
      updateConfig,
      configsValues,
      companyId,
    ],
  );

  return {
    customInputFields,
    isCustomFieldsFetched,
    isSubmitting,
    profileForm,
    openConfirmation,
    setOpenConfirmation,
    closeModal,
    handleClose,
    onSubmit,
  };
}

export default useProfileForm;
